import React, { createContext, useContext, useState, useCallback } from 'react'
import { Snackbar, Alert, AlertColor } from '@mui/material'

interface ToastOptions {
  message: string
  severity?: AlertColor
  duration?: number
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' }
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void
  showSuccess: (message: string) => void
  showError: (message: string) => void
  showWarning: (message: string) => void
  showInfo: (message: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

const DURATION_MAP: Record<AlertColor, number> = {
  success: 3000,
  info: 3000,
  warning: 5000,
  error: 6000,
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [toast, setToast] = useState<ToastOptions>({ message: '', severity: 'info' })

  const showToast = useCallback((options: ToastOptions) => {
    setToast(options)
    setOpen(true)
  }, [])

  const showSuccess = useCallback((message: string) => {
    showToast({ message, severity: 'success' })
  }, [showToast])

  const showError = useCallback((message: string) => {
    showToast({ message, severity: 'error' })
  }, [showToast])

  const showWarning = useCallback((message: string) => {
    showToast({ message, severity: 'warning' })
  }, [showToast])

  const showInfo = useCallback((message: string) => {
    showToast({ message, severity: 'info' })
  }, [showToast])

  const handleClose = useCallback((_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return
    setOpen(false)
  }, [])

  const severity = toast.severity || 'info'
  const duration = toast.duration ?? DURATION_MAP[severity]

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={toast.anchorOrigin ?? { vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
